import type { Config, Report, SessionEntry } from './types'
import { createStore, type State, type Tool } from './state'
import { installErrorRing } from './capture'
import { assembleReport, buildExperience, BUILD, VERSION } from './report'
import { send, download } from './transport'
import { CSS } from './ui/styles'
import { HighlightLayer, elementLabel, elementSpeech } from './ui/highlight'
import { Drawer } from './ui/drawer'
import { Dialog, type DialogResult } from './ui/dialog'
import { showToast } from './ui/toast'
import { Picker } from './picker'
import { Marker, buildRegionTarget } from './marker'
import { captureElement, captureAnnotatedRegion, captureDisplayRegion } from './screenshot'
import { recoverRules } from './css'

const UNDO_MS = 5000

export const version = VERSION
/** Source stamp of this bundle; `QCLens.build` in the console identifies the loaded copy. */
export const build = BUILD
export type { Config, Report }

let config: Config | null = null
let mounted: Mounted | null = null
let hotkeyInstalled = false

interface Mounted {
  host: HTMLElement
  root: ShadowRoot
  store: ReturnType<typeof createStore>
  highlight: HighlightLayer
  drawer: Drawer
  dialog: Dialog
  picker: Picker
  marker: Marker
  live: HTMLElement
  session: SessionEntry[]
  pendingReport: Report | null
  lostTimer: number
  keyHandler: (e: KeyboardEvent) => void
  altGhost: (e: KeyboardEvent) => void
  onHide: () => void
  applyInsets: () => void
  autoCollapsed: boolean
  screenshot: 'dom' | 'display' | false
  /** In-flight report held during the 5s undo window before the real send. */
  outbox: { report: Report; el: Element; values: DialogResult; timer: number } | null
}

export function init(cfg: Config): void {
  if (!cfg || !cfg.transport) throw new Error('QCLens.init requires a transport config')
  config = cfg
  installErrorRing()
  if (cfg.hotkey !== false && !hotkeyInstalled) {
    hotkeyInstalled = true
    window.addEventListener('keydown', (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'Q' || e.key === 'q')) {
        e.preventDefault()
        isActive() ? exit() : enter()
      }
    })
  }
}

export function isActive(): boolean {
  return !!mounted
}

export function enter(): void {
  if (!config) throw new Error('QCLens.init(config) must be called before enter()')
  if (mounted) return

  const host = document.createElement('qc-lens')
  host.style.cssText = 'position:fixed;z-index:2147483000;inset:auto;'
  const root = host.attachShadow({ mode: config.shadow ?? 'closed' })
  const style = document.createElement('style')
  style.textContent = CSS
  const wrap = document.createElement('div')
  wrap.className = 'qc-root'
  root.append(style, wrap)

  const live = document.createElement('div')
  live.className = 'qc-sr'
  live.setAttribute('aria-live', 'polite')
  wrap.appendChild(live)

  const store = createStore((s, prev) => render(s, prev))
  const highlight = new HighlightLayer(root)

  const drawer = new Drawer(root, { dock: config.dock ?? 'right', layout: config.layout ?? 'overlay' }, {
    onToggleArm() {
      const phase = store.get().phase
      if (phase === 'ARMED' || phase === 'TARGETING' || phase === 'DRAWING') store.dispatch({ type: 'DISARM' })
      else if (phase === 'IDLE') store.dispatch({ type: 'ARM' })
    },
    onSelectTool(tool: Tool) {
      const s = store.get()
      const engaged = s.phase === 'ARMED' || s.phase === 'TARGETING' || s.phase === 'DRAWING'
      // Toggle off if re-selecting the already-active tool; else arm with this tool.
      if (engaged && s.tool === tool) store.dispatch({ type: 'DISARM' })
      else store.dispatch({ type: 'ARM', tool })
    },
    onToggleScreenshot() {
      if (!mounted) return
      mounted.screenshot = mounted.screenshot === false ? 'dom' : false
      drawer.setScreenshot(mounted.screenshot)
    },
    onExit: exit,
    onRowClick(entry) {
      const sel = entry.report.target.css_selector
      let el: Element | null = null
      try {
        el = document.querySelector(sel)
      } catch {
        el = null
      }
      if (el) highlight.flash(el, elementLabel(el))
    },
    onRowCopy(entry) {
      navigator.clipboard?.writeText(JSON.stringify(entry.report, null, 2)).catch(() => {})
      announce('Report JSON copied to clipboard')
    },
  })

  const dialog = new Dialog(root, {
    onSubmit(values: DialogResult) {
      void submit(values)
    },
    onCancel() {
      dialog.close()
      store.dispatch({ type: 'ESC' })
      announce('Report discarded.')
    },
    onCrumbClick(el: Element) {
      retarget(el)
    },
    onDirtyChange(dirty: boolean) {
      drawer.setDraft(dirty)
    },
    onRequestScreenshot() {
      return captureScreenshot()
    },
  })
  const applyInsets = () => {
    const fp = drawer.footprint
    dialog.insets = drawer.dock === 'right' ? { left: 0, right: fp } : { left: fp, right: 0 }
  }
  applyInsets()

  const picker = new Picker({
    isArmed() {
      const s = store.get()
      return (s.phase === 'ARMED' || s.phase === 'TARGETING') && s.tool === 'picker'
    },
    isOurs(e: Event) {
      return e.composedPath().includes(host)
    },
    onHover(el) {
      store.dispatch({ type: 'HOVER', element: el })
    },
    onPick(el) {
      store.dispatch({ type: 'SELECT', element: el })
    },
  })

  const marker = new Marker(root, {
    isArmed() {
      const s = store.get()
      return s.tool === 'marker' && (s.phase === 'ARMED' || s.phase === 'TARGETING' || s.phase === 'DRAWING')
    },
    isOurs(e: Event) {
      return e.composedPath().includes(host)
    },
    onDrawStart() {
      store.dispatch({ type: 'DRAW_START' })
    },
    onConfirm(region) {
      store.dispatch({ type: 'DRAW_CONFIRM', region })
    },
    onClear() {
      const s = store.get()
      if (s.phase === 'DRAWING') store.dispatch({ type: 'DRAW_CLEAR' })
    },
  })

  const keyHandler = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return
    const s = store.get()
    if (s.phase === 'SELECTED') {
      // Esc is rescoped: blur a focused textarea first, then close/discard.
      // The dialog owns this so text is never lost without confirmation.
      e.preventDefault()
      e.stopPropagation()
      dialog.handleEsc()
    } else if (s.phase === 'DRAWING') {
      e.preventDefault()
      e.stopPropagation()
      marker.clear()
    } else if (s.phase === 'TARGETING' || s.phase === 'ARMED') {
      e.preventDefault()
      e.stopPropagation()
      store.dispatch({ type: 'ESC' })
    }
  }
  window.addEventListener('keydown', keyHandler, true)

  // Alt-hold ghosts the drawer so a reviewer can see/pick content behind it.
  const altGhost = (e: KeyboardEvent) => {
    if (e.key !== 'Alt') return
    drawer.setGhosted(e.type === 'keydown')
  }
  window.addEventListener('keydown', altGhost)
  window.addEventListener('keyup', altGhost)

  // Flush a report still in its undo window if the page is being hidden/closed,
  // so a deferred submit is never lost to a tab close.
  const onHide = () => {
    if (mounted?.outbox) {
      clearTimeout(mounted.outbox.timer)
      void flushOutbox()
    }
  }
  window.addEventListener('pagehide', onHide)

  mounted = {
    host,
    root,
    store,
    highlight,
    drawer,
    dialog,
    picker,
    marker,
    live,
    session: [],
    pendingReport: null,
    lostTimer: 0,
    keyHandler,
    altGhost,
    onHide,
    applyInsets,
    autoCollapsed: false,
    screenshot: config.screenshot ?? 'dom',
    outbox: null,
  }
  document.body.appendChild(host)
  picker.attach()
  drawer.setScreenshot(mounted.screenshot)
  store.dispatch({ type: 'ENTER' })
}

export function exit(): void {
  if (!mounted) return
  // Flush any report still in its undo window so nothing is silently lost.
  if (mounted.outbox) {
    clearTimeout(mounted.outbox.timer)
    void flushOutbox()
  }
  window.removeEventListener('keydown', mounted.keyHandler, true)
  window.removeEventListener('keydown', mounted.altGhost)
  window.removeEventListener('keyup', mounted.altGhost)
  window.removeEventListener('pagehide', mounted.onHide)
  clearInterval(mounted.lostTimer)
  mounted.picker.detach()
  mounted.marker.destroy()
  mounted.drawer.clearPageOffset() // zero residue: restore the host page's own layout
  mounted.host.remove()
  mounted = null
}

/** Reports filed this session (read-only). */
export function sessionReports(): Report[] {
  return mounted ? mounted.session.map((e) => e.report) : []
}

// ---- internals ----

function retarget(el: Element): void {
  if (!mounted || !config) return
  mounted.store.dispatch({ type: 'RETARGET', element: el })
  capturePending(el)
  mounted.dialog.updateAnchor(el)
  if (mounted.pendingReport) mounted.dialog.showContext(mounted.pendingReport)
  mounted.highlight.show(el, elementLabel(el), { locked: true })
}

/** Capture target/context at click time (ui-ux-plan §5.4: context survives SPA rerenders). */
function capturePending(el: Element): void {
  if (!mounted || !config) return
  const draft = assembleReport(el, { description: '', report_type: 'bug', area: 'functional' }, config)
  mounted.pendingReport = draft
}

/** Assemble a region (marker) draft: ancestor as anchor, region_box + region_elements. */
function captureRegionPending(): void {
  if (!mounted || !config) return
  const region = mounted.store.get().region
  if (!region) return
  const draft = assembleReport(region.ancestor, { description: '', report_type: 'bug', area: 'visual' }, config)
  const rt = buildRegionTarget(region, config.redact ?? [])
  draft.target.kind = 'region'
  draft.target.css_selector = rt.ancestorSelector
  draft.target.dom_path = rt.ancestorPath
  draft.target.region_box = rt.region_box
  draft.target.region_elements = rt.region_elements
  draft.annotation = {
    strokes: region.strokes.map((s) => s.map((p) => ({ x: Math.round(p.x), y: Math.round(p.y) }))),
    coordinate_space: 'page',
  }
  mounted.pendingReport = draft
}

/** Apply the reviewer's choices to the report: experience, excluded context, screenshot. */
function finalizeReport(report: Report, values: DialogResult): void {
  report.experience = {
    ...buildExperience(values),
    ...(values.expected ? { expected: values.expected } : {}),
  }
  applyContextExclusions(report, values.excludedContext)
  if (values.includeScreenshot && values.screenshot) {
    const key = report.target.kind === 'region' ? 'screenshot_annotated' : 'screenshot_region'
    ;(report.attachments ??= {})[key] = values.screenshot
  }
}

/** Strip context items the reviewer excluded — the payload only carries what they saw (§4). */
function applyContextExclusions(report: Report, excluded: string[]): void {
  const drop = new Set(excluded)
  const t = report.target
  const env = report.environment
  if (drop.has('viewport') && env) delete env.viewport
  if (drop.has('user_agent') && env) delete env.user_agent
  if (drop.has('console') && env) delete env.console_errors
  if (drop.has('dom_snippet')) delete t.outer_html_excerpt
  if (drop.has('css')) {
    delete t.matched_rules
    delete t.computed_styles
    delete t.styles_note
  }
  if (drop.has('component')) delete report.source_hints
}

/**
 * Submit with a 5-second undo window: the report is shown as filed and the send
 * is deferred; Undo cancels it and reopens the draft (text intact). Exit/redo
 * flushes it. Nothing is sent until the window elapses.
 */
async function submit(values: DialogResult): Promise<void> {
  if (!mounted || !config || !mounted.pendingReport) return
  const report = mounted.pendingReport
  const el = mounted.store.get().element!
  finalizeReport(report, values)

  mounted.session.push({ report, sentAt: Date.now() })
  mounted.drawer.addSessionEntry({ report, sentAt: Date.now() }, mounted.session.length, true)
  mounted.dialog.close()
  mounted.pendingReport = null
  mounted.store.dispatch({ type: 'SEND_OK' })

  const timer = window.setTimeout(() => void flushOutbox(), UNDO_MS)
  mounted.outbox = { report, el, values, timer }
  showToast(mounted.root, report.report_id, {
    undoMs: UNDO_MS,
    onUndo: () => undoOutbox(),
  })
  announce(`Report filed, ${report.report_id.slice(0, 8)}. Press undo within 5 seconds to edit.`)
}

/** Send the buffered report for real (undo window elapsed or session ending). */
async function flushOutbox(): Promise<void> {
  if (!mounted?.outbox) return
  const { report } = mounted.outbox
  mounted.outbox = null
  try {
    await recoverCrossOriginCss(report)
    await send(report, config!.transport)
  } catch (err) {
    // Deferred send happens after the dialog closed, so failure can't show inline;
    // download the report locally so a session never loses work, and flag the row.
    if (config!.transport.type !== 'download') download(report)
    mounted.drawer.markSessionFailed(report.report_id)
    announce(`Report ${report.report_id.slice(0, 8)} couldn't send — downloaded a copy instead.`)
  }
}

/** Undo a buffered submit within the window: cancel the send and reopen the draft. */
function undoOutbox(): void {
  if (!mounted?.outbox) return
  const { report, el, values } = mounted.outbox
  clearTimeout(mounted.outbox.timer)
  mounted.outbox = null
  mounted.session = mounted.session.filter((e) => e.report.report_id !== report.report_id)
  mounted.drawer.removeSession(report.report_id, mounted.session.length)
  mounted.pendingReport = report
  mounted.store.dispatch({ type: 'SELECT', element: el })
  // render() opens a fresh dialog; restore the reviewer's text/choices.
  mounted.dialog.restore(values)
  announce('Submission undone. Your draft is back.')
}

/** Fetch-recover matched rules from cross-origin sheets (CORS permitting); strip the internal field. */
async function recoverCrossOriginCss(report: Report): Promise<void> {
  const hrefs = report.target._unreadable_sheets
  delete report.target._unreadable_sheets
  if (!hrefs?.length || !report.target.matched_rules) return
  const el = mounted?.store.get().element
  if (!el) return
  try {
    const budget = 24 - report.target.matched_rules.length
    const { rules, stillUnreadable } = await recoverRules(el, hrefs, Math.max(0, budget))
    report.target.matched_rules.push(...rules)
    if (rules.length) {
      report.target.styles_note = stillUnreadable.length
        ? `${rules.length} rule(s) recovered by fetch; ${stillUnreadable.length} stylesheet(s) still unreadable (no CORS)`
        : `${rules.length} cross-origin rule(s) recovered by fetch`
    } else if (stillUnreadable.length) {
      report.target.styles_note = `${stillUnreadable.length} cross-origin stylesheet(s) unreadable (no CORS); computed_styles still reflect them`
    }
  } catch {
    // keep the original note; computed_styles already reflect the resolved effect
  }
}

/** Capture a screenshot for the inline thumbnail (§4). Respects mode + size cap; null if unavailable. */
async function captureScreenshot(): Promise<string | null> {
  if (!mounted || !config) return null
  const mode = mounted.screenshot
  if (mode === false) return null
  const redact = config.redact ?? []
  const maxBytes = config.screenshotMaxBytes ?? 4_000_000
  const s = mounted.store.get()
  try {
    let shot: string | null = null
    if (s.region) {
      shot = mode === 'display' ? await captureDisplayRegion(s.region.box) : await captureAnnotatedRegion(s.region.box, s.region.strokes, redact)
    } else if (s.element) {
      const bb = s.element.getBoundingClientRect()
      shot = mode === 'display'
        ? await captureDisplayRegion({ x: bb.x, y: bb.y, w: bb.width, h: bb.height })
        : await captureElement(s.element, redact)
    }
    if (!shot) return null
    const bytes = Math.floor((shot.length - (shot.indexOf(',') + 1)) * 0.75)
    return bytes > maxBytes ? null : shot
  } catch {
    return null
  }
}

function announce(msg: string): void {
  if (mounted) mounted.live.textContent = msg
}

/** True if the element's box overlaps the (expanded) drawer's screen footprint. */
function elementUnderDrawer(el: Element, drawer: Drawer): boolean {
  // Push layout reflows the page beside the drawer, so nothing sits under it
  // (host position:fixed elements excepted — Alt-ghosting covers those).
  if (drawer.layout === 'push') return false
  const r = el.getBoundingClientRect()
  if (r.width === 0 && r.height === 0) return false
  const edge = drawer.dock === 'right' ? window.innerWidth - drawer.width : drawer.width
  return drawer.dock === 'right' ? r.right > edge : r.left < edge
}

function render(s: State, prev: State): void {
  if (!mounted) return
  const { highlight, dialog, drawer, picker, marker, store } = mounted
  drawer.render(s)
  const pickerEngaged = (s.phase === 'ARMED' || s.phase === 'TARGETING') && s.tool === 'picker'
  const markerEngaged =
    s.tool === 'marker' && (s.phase === 'ARMED' || s.phase === 'TARGETING' || s.phase === 'DRAWING')
  picker.setCursor(pickerEngaged)
  marker.setActive(markerEngaged)

  switch (s.phase) {
    case 'TARGETING':
      if (s.element) {
        highlight.show(s.element, elementLabel(s.element))
        announce(`Selected: ${elementSpeech(s.element)}`)
      }
      break
    case 'SELECTED':
      if (s.region) highlight.hide()
      else if (s.element) highlight.show(s.element, elementLabel(s.element), { locked: true, lost: s.elementLost, spotlight: true })
      break
    case 'ARMED':
    case 'IDLE':
    case 'DRAWING':
      highlight.hide()
      break
    case 'OFF':
      break
  }

  // Dialog lifecycle on phase transitions.
  const entered = (p: State['phase']) => s.phase === p && prev.phase !== p
  if (entered('SELECTED') && s.element) {
    const isRegion = !!s.region
    // Occlusion: if the anchor sits under the drawer, collapse to reveal it.
    if (!drawer.collapsed && elementUnderDrawer(s.element, drawer)) {
      drawer.setCollapsed(true, false)
      mounted.autoCollapsed = true
      mounted.applyInsets()
    }
    if (isRegion) captureRegionPending()
    else capturePending(s.element)
    dialog.open(s.element)
    if (mounted.pendingReport) dialog.showContext(mounted.pendingReport)
    if (mounted.screenshot !== false) void dialog.loadScreenshot()
    announce(
      isRegion
        ? `Region selected, ${s.region!.elements.length} elements. Report dialog open.`
        : `Selected ${elementSpeech(s.element)}. Report dialog open.`,
    )
    clearInterval(mounted.lostTimer)
    mounted.lostTimer = window.setInterval(() => {
      const cur = store.get()
      if (cur.phase === 'SELECTED' && cur.element && !cur.element.isConnected && !cur.elementLost) {
        store.dispatch({ type: 'ELEMENT_LOST' })
      }
    }, 500)
  }
  if (s.phase !== 'SELECTED') {
    clearInterval(mounted.lostTimer)
    if (prev.phase === 'SELECTED') {
      dialog.close()
      if (mounted.autoCollapsed) {
        drawer.setCollapsed(false, false)
        mounted.autoCollapsed = false
        mounted.applyInsets()
      }
    }
  }
  if (s.elementLost && !prev.elementLost) {
    dialog.markElementLost()
    highlight.markLost()
  }
}
